import DateFnsUtils from '@date-io/date-fns';
import { createTheme } from '@material-ui/core';
import { PaletteColor } from '@material-ui/core/styles/createPalette';
import { Shadows } from '@material-ui/core/styles/shadows';
import { DatePicker, KeyboardDatePicker, KeyboardDatePickerProps, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { CSSProperties, ThemeProvider } from '@material-ui/styles';
import React, { useState } from 'react';
import { FieldError } from 'react-hook-form';

import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { CustomTypeText } from '@/config/types';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { mergeDeep } from '@/utils/mergeDeep';
import { formatDateToUTC } from '@/utils/timeFormatters';

interface CustomInputDateProps extends Omit<KeyboardDatePickerProps, 'onChange' | 'value' | 'error'> {
  locale: string;
  field: {
    value: Date | null;
    onChange: (date: Date | null) => void;
  };
  value?: Date | null;
  allowKeyboardPicking: boolean;
  themeParams?: Partial<{
    palette: {
      primary: { main: string; contrastText: string };
      secondary: { main: string; contrastText: string };
      text: CustomTypeText;
      minor: PaletteColor;
      input: PaletteColor;
    };
    shadows: Shadows;
  }>;
  iconStyles?: CSSProperties;
  error?: FieldError;
  keepTimezone?: boolean;
}

export const CustomInputDate = React.forwardRef<HTMLInputElement, CustomInputDateProps>(
  (
    { locale, field, allowKeyboardPicking, themeParams = {}, iconStyles, error, keepTimezone = false, ...props },
    ref,
  ) => {
    const currentTheme = useCustomTheme();
    const { getTextIdTranslation } = useTranslation();
    const [errorState, setErrorState] = useState<string | undefined>(undefined);

    class CustomDateFnsUtils extends DateFnsUtils {}

    const labelStyle = {
      maxWidth: 'calc(100% - 36px)',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
    };

    const inputStyle = {
      fontSize: '0.875rem',
      padding: '10px 0 8px',
    };

    const dateThemeValues = {
      ...currentTheme,
      palette: {
        ...currentTheme.palette,
        primary: {
          main: currentTheme.palette.text.title || '#000',
          contrastText: '#fff',
        },
        secondary: {
          main: currentTheme.palette.text.title || '#000',
          contrastText: '#fff',
        },
      },
      shadows: [
        'none',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '0px 1px 5px 0px #0000001a, 0 20px 80px 5px #0a202d1c',
        '0px 1px 5px 0px #0000001a, 0 20px 80px 5px #0a202d1c',
        '0px 1px 5px 0px #0000001a, 0 20px 80px 5px #0a202d1c',
      ] as Shadows,
    };
    const finalTheme = mergeDeep(dateThemeValues, themeParams);

    const dateValue = field?.value || props?.value;

    const errorProps = () => {
      if (field?.value && isNaN(new Date(field.value).getTime())) {
        return { error: true, helperText: getTextIdTranslation('INVALID_DATE') };
      }
      if (
        props.minDate &&
        field?.value &&
        (props.minDate instanceof Date ? props.minDate.getTime() : new Date(props.minDate as string).getTime()) >=
          field?.value.getTime()
      ) {
        return {
          error: true,
          helperText: getTextIdTranslation('DATE_SHOULD_NOT_BE_BEFORE_MINIMAL_DATE'),
        };
      }
      if (
        props.maxDate &&
        field?.value &&
        (props.maxDate instanceof Date ? props.maxDate.getTime() : new Date(props.maxDate as string).getTime()) <
          field?.value.getTime()
      ) {
        return {
          error: true,
          helperText: getTextIdTranslation('DATE_SHOULD_NOT_BE_AFTER_MAXIMAL_DATE'),
        };
      }
      if (error || errorState) {
        return {
          error: !!error || !!errorState,
          helperText: errorState ? <Text tId={errorState} /> : error?.message,
        };
      }
    };

    return (
      <ThemeProvider theme={createTheme(finalTheme)}>
        <MuiPickersUtilsProvider utils={CustomDateFnsUtils} locale={locale}>
          <div ref={ref} style={{ position: 'relative' }}>
            {allowKeyboardPicking ? (
              <KeyboardDatePicker
                ref={ref}
                {...props}
                InputLabelProps={{
                  ...props.InputLabelProps,
                  style: labelStyle,
                }}
                KeyboardButtonProps={{
                  style: { padding: 0 },
                }}
                inputProps={{ style: inputStyle }}
                clearable
                value={dateValue ?? null}
                onChange={(e) => {
                  if (e && !keepTimezone) {
                    field?.onChange(formatDateToUTC(e));
                    return;
                  }
                  return field.onChange(e);
                }}
                onError={(e) => {
                  if (!e) {
                    return setErrorState(undefined);
                  }
                  if (e) {
                    setErrorState(e.toString());
                  }
                }}
                {...errorProps()}
              />
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <DatePicker
                  {...props}
                  InputLabelProps={{
                    ...props.InputLabelProps,
                    style: labelStyle,
                  }}
                  inputProps={{
                    style: inputStyle,
                  }}
                  value={dateValue ?? null}
                  onChange={(e) => {
                    if (e && !keepTimezone) {
                      field?.onChange(formatDateToUTC(e));
                      return;
                    }
                    return field.onChange(e);
                  }}
                  clearable
                />
                {!props.disabled && (
                  <i
                    className="material-icons-outlined"
                    style={{
                      position: 'absolute',
                      fontSize: '17px',
                      right: '3px',
                      top: '11px',
                      ...iconStyles,
                    }}
                  >
                    calendar_today
                  </i>
                )}
              </div>
            )}
          </div>
        </MuiPickersUtilsProvider>
      </ThemeProvider>
    );
  },
);
